import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const API = '/api';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    plus: ['M12 5v14', 'M5 12h14'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
    download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3',
};

export default function Routes({ user }) {
    const [routes, setRoutes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [modal, setModal] = useState(null);
    const [deleteModal, setDeleteModal] = useState(null);
    const [zones, setZones] = useState([]);
    const [clients, setClients] = useState([]);
    const [importing, setImporting] = useState(false);
    const fileInputRef = useRef(null);

    const operatorId = user?.operatorId || user?._id || 'all';

    useEffect(() => {
        fetchData();
        fetchZones();
        fetchClients();
    }, []);

    const fetchClients = async () => {
        try {
            const res = await axios.get(`${API}/clients/${operatorId}`);
            setClients(res.data);
        } catch (err) { console.error(err); }
    };

    const fetchZones = async () => {
        try {
            const res = await axios.get(`${API}/master-data`);
            setZones(res.data.filter(z => z.category === 'route-zone'));
        } catch (err) { console.error(err); }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/routes/${operatorId}`);
            setRoutes(res.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                'Route Name': 'IT Park Shuttle',
                'Pickup Location': 'Downtown Station',
                'Drop Location': 'IT Park Main Gate',
                'Distance': 5.2,
                'Estimated Minutes': 15,
                'Client': 'Infosys Ltd',
                'Description': 'Regular staff shuttle',
                'Status': 'Active'
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Routes');
        XLSX.writeFile(wb, 'Routes_Template.xlsx');
    };

    const handleImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setImporting(true);
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const data = XLSX.utils.sheet_to_json(ws);

                const mapped = data.map(row => ({
                    name: row['Route Name'],
                    pickupLocation: row['Pickup Location'],
                    dropLocation: row['Drop Location'],
                    distance: row['Distance'],
                    estimatedMinutes: row['Estimated Minutes'],
                    client: row['Client'],
                    description: row['Description'],
                    status: row['Status'] || 'Active'
                }));

                const res = await axios.post(`${API}/routes/import`, { operatorId, routes: mapped });
                if (res.status === 201) {
                    alert(`${res.data.successCount} routes imported!`);
                    fetchData();
                }
            } catch (err) {
                alert('Import failed. Please check your data format.');
            } finally {
                setImporting(false);
                e.target.value = '';
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleSave = async (formData) => {
        try {
            const payload = { ...formData, operatorId };
            if (modal.data?._id) {
                await axios.put(`${API}/routes/${modal.data._id}`, payload);
            } else {
                await axios.post(`${API}/routes`, payload);
            }
            setModal(null);
            fetchData();
        } catch (err) { console.error(err); }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${API}/routes/${deleteModal._id}`);
            setDeleteModal(null);
            fetchData();
        } catch (err) { console.error(err); }
    };

    const filtered = routes.filter(r =>
        r.name.toLowerCase().includes(search.toLowerCase()) ||
        r.pickupLocation.toLowerCase().includes(search.toLowerCase()) ||
        r.dropLocation.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="md-page">
            <div className="md-header">
                <div>
                    <h1 className="md-title">Routes Management</h1>
                    <p className="md-subtitle">Create and manage transport routes for your operations.</p>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <input type="file" ref={fileInputRef} hidden accept=".xlsx,.xls" onChange={handleImport} />
                    <button className="premium-btn btn-import" onClick={() => fileInputRef.current.click()} disabled={importing}>
                        <Icon d={icons.upload} size={18} strokeWidth={2.5} />
                        {importing ? 'Importing...' : 'Import'}
                    </button>
                    <button className="premium-btn btn-template" onClick={downloadTemplate}>
                        <Icon d={icons.download} size={18} strokeWidth={2.5} />
                        Template
                    </button>
                    <button className="premium-btn btn-primary-action" onClick={() => setModal({ data: null })}>
                        <Icon d={icons.plus} size={18} strokeWidth={2.5} />
                        Add Route
                    </button>
                </div>
            </div>

            <div className="md-panel">
                <div className="md-panel-toolbar">
                    <div className="md-search-wrap">
                        <Icon d={icons.search} size={14} />
                        <input
                            className="md-input md-search"
                            placeholder="Search routes..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                {loading ? (
                    <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>Loading routes...</div>
                ) : (
                    <div className="md-table-wrap">
                        <table className="md-table">
                            <thead>
                                <tr>
                                    <th className="md-th">Route Name</th>
                                    <th className="md-th">Pickup Location</th>
                                    <th className="md-th">Drop Location</th>
                                    <th className="md-th">Client</th>
                                    <th className="md-th">Zone</th>
                                    <th className="md-th">Status</th>
                                    <th className="md-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(r => (
                                    <tr key={r._id}>
                                        <td className="md-td"><strong>{r.name}</strong></td>
                                        <td className="md-td">{r.pickupLocation}</td>
                                        <td className="md-td">{r.dropLocation}</td>
                                        <td className="md-td">{r.clientId?.companyName || '—'}</td>
                                        <td className="md-td">{r.zone?.name || '—'}</td>
                                        <td className="md-td">
                                            <span className={`md-status-badge ${r.status === 'Active' ? 'md-badge-active' : 'md-badge-inactive'}`}>
                                                {r.status}
                                            </span>
                                        </td>
                                        <td className="md-td">
                                            <div className="md-actions">
                                                <button className="md-icon-btn" onClick={() => setModal({ data: r })}><Icon d={icons.edit} size={14} /></button>
                                                <button className="md-icon-btn md-icon-btn-danger" onClick={() => setDeleteModal(r)}><Icon d={icons.trash} size={14} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            {modal && (
                <RouteForm
                    data={modal.data}
                    zones={zones}
                    clients={clients}
                    onClose={() => setModal(null)}
                    onSave={handleSave}
                />
            )}

            {/* Delete Confirmation */}
            {deleteModal && (
                <div className="md-modal-overlay" onClick={() => setDeleteModal(null)}>
                    <div className="md-modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
                        <div className="md-modal-header">
                            <h3 className="md-modal-title" style={{ color: '#ef4444' }}>Confirm Delete</h3>
                        </div>
                        <div className="md-modal-body">
                            Are you sure you want to delete route "<strong>{deleteModal.name}</strong>"? This action cannot be undone.
                        </div>
                        <div className="md-modal-footer">
                            <button className="md-cancel-btn" onClick={() => setDeleteModal(null)}>Cancel</button>
                            <button className="md-save-btn" style={{ background: '#ef4444' }} onClick={handleDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function RouteForm({ data, zones, clients, onClose, onSave }) {
    const [form, setForm] = useState(data ? {
        ...data,
        zone: data.zone?._id || data.zone || '',
        clientId: data.clientId?._id || data.clientId || ''
    } : {
        name: '',
        pickupLocation: '',
        pickupLat: null,
        pickupLng: null,
        dropLocation: '',
        dropLat: null,
        dropLng: null,
        distance: '',
        estimatedMinutes: '',
        zone: '',
        clientId: '',
        description: '',
        status: 'Active'
    });

    const mapRef = useRef(null);
    const pickupInputRef = useRef(null);
    const dropInputRef = useRef(null);
    const googleMap = useRef(null);
    const directionsRenderer = useRef(null);
    const directionsService = useRef(null);

    // Initialize Map, Autocomplete and Directions
    useEffect(() => {
        if (!window.google) return;

        // Initialize Map
        googleMap.current = new window.google.maps.Map(mapRef.current, {
            center: { lat: 20.5937, lng: 78.9629 }, // India center
            zoom: 5,
            disableDefaultUI: true,
            zoomControl: true,
            styles: [
                { "featureType": "all", "elementType": "labels.text.fill", "stylers": [{ "color": "#747474" }] },
                { "featureType": "all", "elementType": "labels.text.stroke", "stylers": [{ "visibility": "off" }] }
            ]
        });

        directionsService.current = new window.google.maps.DirectionsService();
        directionsRenderer.current = new window.google.maps.DirectionsRenderer({
            map: googleMap.current,
            suppressMarkers: false,
            polylineOptions: {
                strokeColor: '#4f46e5',
                strokeWeight: 5
            }
        });

        // Autocomplete setup
        const options = { fields: ["formatted_address", "geometry", "name"] };
        const pickupAutocomplete = new window.google.maps.places.Autocomplete(pickupInputRef.current, options);
        const dropAutocomplete = new window.google.maps.places.Autocomplete(dropInputRef.current, options);

        pickupAutocomplete.addListener('place_changed', () => {
            const place = pickupAutocomplete.getPlace();
            if (place.geometry) {
                setForm(prev => ({
                    ...prev,
                    pickupLocation: place.formatted_address || place.name,
                    pickupLat: place.geometry.location.lat(),
                    pickupLng: place.geometry.location.lng()
                }));
            }
        });

        dropAutocomplete.addListener('place_changed', () => {
            const place = dropAutocomplete.getPlace();
            if (place.geometry) {
                setForm(prev => ({
                    ...prev,
                    dropLocation: place.formatted_address || place.name,
                    dropLat: place.geometry.location.lat(),
                    dropLng: place.geometry.location.lng()
                }));
            }
        });
    }, []);

    // Calculate route when locations change
    useEffect(() => {
        const calculateRoute = () => {
            if (!window.google || !directionsService.current) return;

            // Use coordinates if available, otherwise fallback to address string
            const origin = (form.pickupLat && form.pickupLng)
                ? { lat: Number(form.pickupLat), lng: Number(form.pickupLng) }
                : form.pickupLocation;
            const destination = (form.dropLat && form.dropLng)
                ? { lat: Number(form.dropLat), lng: Number(form.dropLng) }
                : form.dropLocation;

            if (!origin || !destination || (typeof origin === 'string' && origin.length < 3) || (typeof destination === 'string' && destination.length < 3)) return;

            directionsService.current.route({
                origin: origin,
                destination: destination,
                travelMode: window.google.maps.TravelMode.DRIVING
            }, (result, status) => {
                if (status === window.google.maps.DirectionsStatus.OK) {
                    directionsRenderer.current.setDirections(result);
                    const route = result.routes[0];
                    if (route && route.legs && route.legs[0]) {
                        const distKm = (route.legs[0].distance.value / 1000).toFixed(2);
                        const durationMin = Math.round(route.legs[0].duration.value / 60);
                        // Auto-fill distance and minutes
                        setForm(prev => ({ ...prev, distance: distKm, estimatedMinutes: durationMin }));
                    }
                }
            });
        };

        const timer = setTimeout(calculateRoute, 500); // Small debounce to avoid excessive calls
        return () => clearTimeout(timer);
    }, [form.pickupLocation, form.dropLocation, form.pickupLat, form.dropLat]);

    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal" style={{ maxWidth: 1100, width: '95%' }} onClick={e => e.stopPropagation()}>
                <div className="md-modal-header">
                    <h3 className="md-modal-title">{data ? 'Edit Route' : 'Add New Route'}</h3>
                    <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={18} /></button>
                </div>
                <div className="md-modal-body" style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: '24px', padding: '24px' }}>
                    <div className="form-fields" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div className="md-form-group">
                            <label className="md-label">Route Name*</label>
                            <input className="md-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. IT Park Shuttle" />
                        </div>

                        <div className="md-form-group">
                            <label className="md-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                Pickup Location*
                                {form.pickupLat && <span style={{ fontSize: '10px', color: '#10b981' }}>Selected</span>}
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    ref={pickupInputRef}
                                    className="md-input"
                                    value={form.pickupLocation}
                                    onChange={e => setForm({ ...form, pickupLocation: e.target.value, pickupLat: null, pickupLng: null })}
                                    placeholder="Search for pickup point..."
                                    style={{ paddingLeft: '32px' }}
                                />
                                <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                                    <Icon d={icons.search} size={14} />
                                </div>
                            </div>
                        </div>

                        <div className="md-form-group">
                            <label className="md-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                Drop Location*
                                {form.dropLat && <span style={{ fontSize: '10px', color: '#10b981' }}>Selected</span>}
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    ref={dropInputRef}
                                    className="md-input"
                                    value={form.dropLocation}
                                    onChange={e => setForm({ ...form, dropLocation: e.target.value, dropLat: null, dropLng: null })}
                                    placeholder="Search for destination..."
                                    style={{ paddingLeft: '32px' }}
                                />
                                <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                                    <Icon d={icons.search} size={14} />
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div className="md-form-group">
                                <label className="md-label">Distance (km)</label>
                                <input
                                    type="number"
                                    className="md-input"
                                    value={form.distance}
                                    onChange={e => setForm({ ...form, distance: e.target.value })}
                                    placeholder="0.00"
                                    step="0.01"
                                />
                            </div>
                            <div className="md-form-group">
                                <label className="md-label">Est. Minutes</label>
                                <input
                                    type="number"
                                    className="md-input"
                                    value={form.estimatedMinutes}
                                    onChange={e => setForm({ ...form, estimatedMinutes: e.target.value })}
                                    placeholder="Minutes"
                                />
                            </div>
                        </div>

                        <div className="md-form-group">
                            <label className="md-label">Zone</label>
                            <select className="md-input" value={form.zone} onChange={e => setForm({ ...form, zone: e.target.value })}>
                                <option value="">Select Zone</option>
                                {zones.map(z => <option key={z._id} value={z._id}>{z.name}</option>)}
                            </select>
                        </div>

                        <div className="md-form-group">
                            <label className="md-label">Client</label>
                            <select className="md-input" value={form.clientId} onChange={e => setForm({ ...form, clientId: e.target.value })}>
                                <option value="">Select Client</option>
                                {clients.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                            </select>
                        </div>

                        <div className="md-form-group">
                            <label className="md-label">Description</label>
                            <textarea className="md-textarea" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Add extra details here..." />
                        </div>

                        <div className="md-form-group">
                            <label className="md-label">Status</label>
                            <select className="md-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                            </select>
                        </div>
                    </div>

                    <div className="map-wrapper" style={{ position: 'relative', minHeight: '500px', display: 'flex', flexDirection: 'column' }}>
                        <div ref={mapRef} style={{ flex: 1, background: '#f1f5f9', borderRadius: '16px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                            {!window.google && (
                                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                                    Loading Google Maps...
                                </div>
                            )}
                        </div>
                        <div style={{ marginTop: '12px', padding: '12px', background: '#f8fafc', borderRadius: '8px', fontSize: '12px', color: '#64748b', border: '1px solid #e2e8f0' }}>
                            ℹ️ Locations selected via search will be visualized on the map instantly.
                        </div>
                    </div>
                </div>
                <div className="md-modal-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '16px 24px' }}>
                    <button className="md-cancel-btn" onClick={onClose}>Cancel</button>
                    <button
                        className="md-save-btn"
                        onClick={() => onSave(form)}
                        disabled={!form.name || !form.pickupLocation || !form.dropLocation}
                        style={{ opacity: (!form.name || !form.pickupLocation || !form.dropLocation) ? 0.7 : 1 }}
                    >
                        {data ? 'Update Route' : 'Create Route'}
                    </button>
                </div>
            </div>
        </div>
    );
}
